import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams, Outlet } from "react-router-dom";
import { Layout } from "./components/Layout";

const AdminApp = lazy(() =>
  import("./admin/AdminApp").then((m) => ({ default: m.AdminApp })),
);
import { HomePage } from "./pages/HomePage";
import { CasinoListPage } from "./pages/CasinoListPage";
import { CasinoReviewPage } from "./pages/CasinoReviewPage";
import { LicensedCasinoPage } from "./pages/LicensedCasinoPage";
import { UnlicensedCasinoPage } from "./pages/UnlicensedCasinoPage";
import { CasinoUtanSpelpausPage } from "./pages/CasinoUtanSpelpausPage";
import { NewCasinosPage } from "./pages/NewCasinosPage";
import { PaymentsHubPage } from "./pages/PaymentsHubPage";
import { PaymentMethodPage } from "./pages/PaymentMethodPage";
import { BonusHubPage } from "./pages/BonusHubPage";
import { FreeSpinsPage } from "./pages/FreeSpinsPage";
import { SlotsHubPage } from "./pages/SlotsHubPage";
import { SlotPage } from "./pages/SlotPage";
import { ProvidersHubPage } from "./pages/ProvidersHubPage";
import { ProviderPage } from "./pages/ProviderPage";
import { LiveCasinoPage } from "./pages/LiveCasinoPage";
import { GuidesHubPage } from "./pages/GuidesHubPage";
import { GuidePage } from "./pages/GuidePage";
import { BettingHubPage } from "./pages/BettingHubPage";
import { EsportsHubPage } from "./pages/EsportsHubPage";
import { ResponsibleGamblingPage } from "./pages/ResponsibleGamblingPage";
import { SpelpausInfoPage } from "./pages/SpelpausInfoPage";
import { StodlinjenInfoPage } from "./pages/StodlinjenInfoPage";
import { SpelinspektionenInfoPage } from "./pages/SpelinspektionenInfoPage";
import { LicenseHubPage } from "./pages/LicenseHubPage";
import { LicensePage } from "./pages/LicensePage";
import { BlacklistPage } from "./pages/BlacklistPage";
import { AboutPage } from "./pages/AboutPage";
import { ContactPage } from "./pages/ContactPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { AffiliatePage } from "./pages/AffiliatePage";
import { NotFoundPage } from "./pages/NotFoundPage";

function LangParamGuard() {
  const { lang } = useParams<{ lang: string }>();
  if (lang !== "sv" && lang !== "en") return <Navigate to="/sv" replace />;
  return null;
}

function LangLayout() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

const svChildRoutes = [
  { path: "", element: <HomePage /> },
  { path: "casino", element: <CasinoListPage /> },
  { path: "casino-online", element: <CasinoListPage /> },
  { path: "basta-casino", element: <CasinoListPage variant="best" /> },
  { path: "svenska-casinon", element: <CasinoListPage variant="swedish" /> },
  { path: "casino-med-svensk-licens", element: <LicensedCasinoPage /> },
  { path: "casino-utan-svensk-licens", element: <UnlicensedCasinoPage /> },
  { path: "casino-utan-spelpaus", element: <CasinoUtanSpelpausPage /> },
  { path: "casino-utan-bankid", element: <UnlicensedCasinoPage variant="bankid" /> },
  { path: "casino-utan-konto", element: <UnlicensedCasinoPage variant="konto" /> },
  { path: "casino-utan-registrering", element: <UnlicensedCasinoPage variant="registrering" /> },
  { path: "nya-casinon", element: <NewCasinosPage /> },
  { path: "casino-bonus", element: <BonusHubPage /> },
  { path: "free-spins", element: <FreeSpinsPage /> },
  { path: "betalningsmetoder", element: <PaymentsHubPage /> },
  { path: "betalningsmetoder/:method", element: <PaymentMethodPage /> },
  { path: "betalningsmetoder/:method/casino", element: <PaymentMethodPage variant="casino" /> },
  { path: "betalningsmetoder/:method/utan-svensk-licens", element: <PaymentMethodPage variant="unlicensed" /> },
  { path: "spelautomater", element: <SlotsHubPage /> },
  { path: "spelautomater/:slot", element: <SlotPage /> },
  { path: "live-casino", element: <LiveCasinoPage /> },
  { path: "speltillverkare", element: <ProvidersHubPage /> },
  { path: "speltillverkare/:slug", element: <ProviderPage /> },
  { path: "spelbolag", element: <CasinoListPage /> },
  { path: "betting", element: <BettingHubPage /> },
  { path: "betting/:topic", element: <BettingHubPage /> },
  { path: "esport-betting", element: <EsportsHubPage /> },
  { path: "esport-betting/:game", element: <EsportsHubPage /> },
  { path: "guider", element: <GuidesHubPage /> },
  { path: "guider/:slug", element: <GuidePage /> },
  { path: "ansvarsfullt-spelande", element: <ResponsibleGamblingPage /> },
  { path: "spelpaus", element: <SpelpausInfoPage /> },
  { path: "stodlinjen", element: <StodlinjenInfoPage /> },
  { path: "spelinspektionen", element: <SpelinspektionenInfoPage /> },
  { path: "licenser", element: <LicenseHubPage /> },
  { path: "licenser/:slug", element: <LicensePage /> },
  { path: "svartlistade-casinon", element: <BlacklistPage /> },
  { path: "recensioner/:slug", element: <CasinoReviewPage /> },
  { path: "om-oss", element: <AboutPage /> },
  { path: "kontakt", element: <ContactPage /> },
  { path: "anvandarvillkor", element: <TermsPage /> },
  { path: "integritetspolicy", element: <PrivacyPage /> },
  { path: "affiliate", element: <AffiliatePage /> },
  { path: "*", element: <NotFoundPage /> },
];

const enChildRoutes = [
  { path: "", element: <HomePage /> },
  { path: "casino", element: <CasinoListPage /> },
  { path: "online-casinos", element: <CasinoListPage /> },
  { path: "best-casinos", element: <CasinoListPage variant="best" /> },
  { path: "swedish-casinos", element: <CasinoListPage variant="swedish" /> },
  { path: "casinos-with-swedish-license", element: <LicensedCasinoPage /> },
  { path: "casinos-without-swedish-license", element: <UnlicensedCasinoPage /> },
  { path: "casinos-without-spelpaus", element: <CasinoUtanSpelpausPage /> },
  { path: "casinos-without-bankid", element: <UnlicensedCasinoPage variant="bankid" /> },
  { path: "casinos-without-account", element: <UnlicensedCasinoPage variant="konto" /> },
  { path: "casinos-without-registration", element: <UnlicensedCasinoPage variant="registrering" /> },
  { path: "new-casinos", element: <NewCasinosPage /> },
  { path: "casino-bonus", element: <BonusHubPage /> },
  { path: "free-spins", element: <FreeSpinsPage /> },
  { path: "payment-methods", element: <PaymentsHubPage /> },
  { path: "payment-methods/:method", element: <PaymentMethodPage /> },
  { path: "payment-methods/:method/casinos", element: <PaymentMethodPage variant="casino" /> },
  { path: "payment-methods/:method/without-swedish-license", element: <PaymentMethodPage variant="unlicensed" /> },
  { path: "slots", element: <SlotsHubPage /> },
  { path: "slots/:slot", element: <SlotPage /> },
  { path: "live-casino", element: <LiveCasinoPage /> },
  { path: "software-providers", element: <ProvidersHubPage /> },
  { path: "software-providers/:slug", element: <ProviderPage /> },
  { path: "sportsbooks", element: <CasinoListPage /> },
  { path: "betting", element: <BettingHubPage /> },
  { path: "betting/:topic", element: <BettingHubPage /> },
  { path: "esports-betting", element: <EsportsHubPage /> },
  { path: "esports-betting/:game", element: <EsportsHubPage /> },
  { path: "guides", element: <GuidesHubPage /> },
  { path: "guides/:slug", element: <GuidePage /> },
  { path: "responsible-gambling", element: <ResponsibleGamblingPage /> },
  { path: "spelpaus", element: <SpelpausInfoPage /> },
  { path: "stodlinjen", element: <StodlinjenInfoPage /> },
  { path: "swedish-gambling-authority", element: <SpelinspektionenInfoPage /> },
  { path: "licenses", element: <LicenseHubPage /> },
  { path: "licenses/:slug", element: <LicensePage /> },
  { path: "blacklisted-casinos", element: <BlacklistPage /> },
  { path: "reviews/:slug", element: <CasinoReviewPage /> },
  { path: "about", element: <AboutPage /> },
  { path: "contact", element: <ContactPage /> },
  { path: "terms", element: <TermsPage /> },
  { path: "privacy", element: <PrivacyPage /> },
  { path: "affiliate", element: <AffiliatePage /> },
  { path: "*", element: <NotFoundPage /> },
];

export default function App() {
  return (
    <Routes>
      <Route
        path="/admin/*"
        element={
          <Suspense
            fallback={
              <div className="grid min-h-screen place-items-center bg-ink-50 text-ink-500">
                Loading admin…
              </div>
            }
          >
            <AdminApp />
          </Suspense>
        }
      />
      <Route path="/sv" element={<LangLayout />}>
        {svChildRoutes.map((r) =>
          r.path === "" ? (
            <Route key="sv-index" index element={r.element} />
          ) : (
            <Route key={`sv-${r.path}`} path={r.path} element={r.element} />
          ),
        )}
      </Route>
      <Route path="/en" element={<LangLayout />}>
        {enChildRoutes.map((r) =>
          r.path === "" ? (
            <Route key="en-index" index element={r.element} />
          ) : (
            <Route key={`en-${r.path}`} path={r.path} element={r.element} />
          ),
        )}
      </Route>
      <Route
        path="/__prerender-404"
        element={
          <Layout>
            <NotFoundPage />
          </Layout>
        }
      />
      <Route path="/:lang/*" element={<LangParamGuard />} />
      <Route
        path="*"
        element={
          <Layout>
            <HomePage />
          </Layout>
        }
      />
    </Routes>
  );
}
